import { ChangeDetectionStrategy, Component } from '@angular/core';
import { WorkflowModel } from '@models/workflow.model';
import { TranslateService } from '@ngx-translate/core';
import { FlowChartComponent } from 'src/app/shared/flow-chart/flow-chart.component';
import {
  FlowConfig,
  FlowNodeTypes,
  WorkflowActionGroups,
} from 'src/app/shared/flow-chart/flow-chart.model';

@Component({
  selector: 'app-workflow',
  imports: [FlowChartComponent],
  templateUrl: './workflow.component.html',
  styleUrl: './workflow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowComponent {
  public config: FlowConfig = {
    triggers: [
      {
        nodeType: FlowNodeTypes.Trigger,
        name: this.translateService.instant('FLOW_CHART.NODES.WEBHOOK.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.Webhook,
        icon: 'ui-icon-forward-3',
      },
      {
        nodeType: FlowNodeTypes.Trigger,
        name: this.translateService.instant('FLOW_CHART.NODES.ALERTTRIGGER.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.AlertTrigger,
        icon: 'ui-icon-notifications-no-stroke',
      },
      {
        nodeType: FlowNodeTypes.Trigger,
        name: this.translateService.instant('FLOW_CHART.NODES.MANUAL_TRIGGER.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.ManualTrigger,
        icon: 'ui-icon-zone-region',
      },
      {
        nodeType: FlowNodeTypes.Trigger,
        name: this.translateService.instant('FLOW_CHART.NODES.SCHEDULE_TRIGGER.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.ScheduleTrigger,
        icon: 'ui-icon-calendar',
      },
    ],
    actions: [
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.CONDITION.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.Condition,
        actionGroup: WorkflowActionGroups.Flow,
        icon: 'ui-icon-order-filter',
        outputNames: [
          this.translateService.instant(
            'FLOW_CHART.NODES.CONDITION.OUTPUT_TRUE',
          ),
          this.translateService.instant(
            'FLOW_CHART.NODES.CONDITION.OUTPUT_FALSE',
          ),
        ],
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.ECHO.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.Echo,
        icon: 'ui-icon-expand-arrow-old',
        actionGroup: WorkflowActionGroups.Actions,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.WAITWEBHOOK.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.WaitWebhook,
        icon: 'ui-icon-time',
        actionGroup: WorkflowActionGroups.Wait,
        outputNames: [
          this.translateService.instant(
            'FLOW_CHART.NODES.WAITWEBHOOK.OUTPUT_SIGNAL',
          ),
          this.translateService.instant(
            'FLOW_CHART.NODES.WAITWEBHOOK.OUTPUT_TIMEOUT',
          ),
        ],
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.WAITALERT.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.WaitAlert,
        actionGroup: WorkflowActionGroups.Wait,
        icon: 'ui-icon-time',
        outputNames: [
          this.translateService.instant(
            'FLOW_CHART.NODES.WAITALERT.OUTPUT_SIGNAL',
          ),
          this.translateService.instant(
            'FLOW_CHART.NODES.WAITALERT.OUTPUT_TIMEOUT',
          ),
        ],
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.HTTPREQ.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.HttpRequest,
        icon: 'ui-icon-globe',
        actionGroup: WorkflowActionGroups.Actions,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.SEND_SMS.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.SendSms,
        actionGroup: WorkflowActionGroups.Notifications,
        icon: 'ui-icon-comment',
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.SEND_EMAIL.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.SendEmail,
        icon: 'ui-icon-mail',
        actionGroup: WorkflowActionGroups.Notifications,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.WAIT_SMS.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.WaitSms,
        icon: 'ui-icon-time',
        outputNames: [
          this.translateService.instant(
            'FLOW_CHART.NODES.WAIT_SMS.OUTPUT_SIGNAL',
          ),
          this.translateService.instant(
            'FLOW_CHART.NODES.WAIT_SMS.OUTPUT_TIMEOUT',
          ),
        ],
        actionGroup: WorkflowActionGroups.Wait,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.WAIT_EMAIL.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.WaitEmail,
        icon: 'ui-icon-time',
        outputNames: [
          this.translateService.instant(
            'FLOW_CHART.NODES.WAIT_EMAIL.OUTPUT_SIGNAL',
          ),
          this.translateService.instant(
            'FLOW_CHART.NODES.WAIT_EMAIL.OUTPUT_TIMEOUT',
          ),
        ],
        actionGroup: WorkflowActionGroups.Wait,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.WAIT.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.Wait,
        icon: 'ui-icon-time',
        outputNames: [
          this.translateService.instant(
            'FLOW_CHART.NODES.WAIT_EMAIL.OUTPUT_SIGNAL',
          ),
          this.translateService.instant(
            'FLOW_CHART.NODES.WAIT_EMAIL.OUTPUT_TIMEOUT',
          ),
        ],
        actionGroup: WorkflowActionGroups.Wait,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.MOBILE_PUSH.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.MobilePush,
        icon: 'ui-icon-cat-custom',
        actionGroup: WorkflowActionGroups.Notifications,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.WAIT_MOBILE_PUSH.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.WaitMobilePush,
        icon: 'ui-icon-time',
        outputNames: [
          this.translateService.instant(
            'FLOW_CHART.NODES.WAIT_MOBILE_PUSH.OUTPUT_SIGNAL',
          ),
          this.translateService.instant(
            'FLOW_CHART.NODES.WAIT_MOBILE_PUSH.OUTPUT_TIMEOUT',
          ),
        ],
        actionGroup: WorkflowActionGroups.Wait,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.SET_FPS.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.SetFps,
        icon: 'ui-icon-camera-control-quality',
        actionGroup: WorkflowActionGroups.Video_Processing,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.TRIM.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.Trim,
        icon: 'ui-icon-camera-shortcut',
        actionGroup: WorkflowActionGroups.Video_Processing,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.CROP.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.Crop,
        icon: 'ui-icon-fullscreen-2',
        actionGroup: WorkflowActionGroups.Video_Processing,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.RESIZE.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.Resize,
        icon: 'ui-icon-alert-type',
        actionGroup: WorkflowActionGroups.Video_Processing,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.VIDEO.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.Video,
        icon: 'ui-icon-camera',
        actionGroup: WorkflowActionGroups.Video_Processing,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.GET_IMAGE.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.GetImage,
        icon: 'ui-icon-image',
        actionGroup: WorkflowActionGroups.Video_Processing,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.AI.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.AI,
        icon: 'ui-icon-free-text-object',
        actionGroup: WorkflowActionGroups.AI,
      },
      {
        nodeType: FlowNodeTypes.Action,
        name: this.translateService.instant('FLOW_CHART.NODES.JS_CODE.NAME'),
        type: WorkflowModel.WorkflowNodeTypes.JsCode,
        icon: 'ui-icon-terminal',
        actionGroup: WorkflowActionGroups.Actions,
      },
    ],
  };

  constructor(private translateService: TranslateService) { }
}
